import { toast } from "../components/toast/toast";
import { generateQueryString } from "../methods/generateQueryString";
import API from "./const";
import { getAuthHeader } from "./utils/getAuthHeader";

export const PLACE_SEARCH_QUERY_LENGTH = 4;

function getPlacesApiFetchHeaders(option: LocationSearchParamsOption): HeadersInit {
    const headers: Record<string, string> = {
        ...(getAuthHeader() as Record<string, string> | undefined)
    };

    // SSR (getServerSideProps, etc.) — no CORS; backend may expect these headers.
    // Browser fetch must omit User-Agent/Origin/Referer — Safari preflights them.
    if (typeof window === "undefined") {
        const origin = option.origin ?? "http://localhost:3000";
        headers.origin = origin;
        headers.referer = `${origin}/`;
        headers["user-agent"] = API.VERCEL_USER_AGENT;
    }

    return headers;
}

export async function searchUsingPlacesAPI(query: string, option: LocationSearchParamsOption) {
    const { source, allowState: is_state_allowed, tag } = option ?? {};
    try {
        if (!query || query.length < PLACE_SEARCH_QUERY_LENGTH) {
            return [];
        }
        const queryParams =
            "&" +
            generateQueryString({
                tag,
                source,
                is_state_allowed,
                session_token: option?.searchSessionToken
            });

        const call = await fetch(encodeURI(API.PLACES_API_SEARCH + query + queryParams), {
            headers: getPlacesApiFetchHeaders(option)
        });
        if (call.status === 200) {
            return (await call.json())?.data ?? [];
        } else {
            const errorBody = await call.json().catch(() => ({}));
            throw new Error(errorBody?.message || call.statusText || "Places search failed");
        }
    } catch (e) {
        if (!option?.suppressErrorToast) {
            toast.error("An error occured, please try again after sometime");
        }
        return [];
    }
}

export async function getPlaceIdForStringQuery(
    ...args: Parameters<typeof searchUsingPlacesAPI>
): Promise<string | undefined> {
    const [query] = args;
    if (query) {
        return (await searchUsingPlacesAPI(...args))?.[0]?.place_id as string;
    }
    return undefined;
}

export interface LocationSearchParamsOption {
    source: LocationSearchSource;
    allowState?: boolean;
    tag?: ("point_locations" | "sublocality" | "locality")[];
    origin?: string;
    searchSessionToken?: string;
    /** When true, caller handles errors (e.g. getCurrentLocation). */
    suppressErrorToast?: boolean;
}

export enum LocationSearchSource {
    LoadSourcePosting = "load_source_posting",
    LoadDestinationPosting = "load_destination_posting",
    LoadWaypointPosting = "load_waypoint_posting",
    LorryPostingSrc = "lorry_posting_src",
    LorryPostingDest = "lorry_posting_dest",
    LoadMarketPlaceSearchSource = "load_marketplace_search_source",
    LoadMarketPlaceSearchDestination = "load_marketplace_search_destination",
    LorryMarketPlaceSearchSource = "lorry_marketplace_search_source",
    LorryMarketPlaceSearchDestination = "lorry_marketplace_search_destination",
    CompanyRegistration = "company_registration",
    LoadPickUpAddress = "load_pickup_address",
    LoadDropAddress = "load_drop_address",
    CompanyOfficeAddress = "company_office_address",
    GpsAddition = "gps_addition",
    CargoPurchase = "cargo_purchase",
    //
    AdminNewCsUser = "admin_new_cs_user",
    TransporterProfile = "transporter_profile",
    CityLoadSearchSource = "city_load_search_source",
    CityLoadSearchDestination = "city_load_search_destination",
    CityLorrySearchSource = "city_lorry_search_source",
    CityLorrySearchDestination = "city_lorry_search_destination",
    CompanyCurrentLocation = "company_current_location",
    CompanyAddRoute = "company_add_route",
    HomepageV2LoadSourcePosting = "homepage_v2_load_source_posting",
    HomepageV2LoadDestinationPosting = "homepage_v2_load_destination_posting"
}
